"use client";

import React from "react";
import { useGetAllFloorPlanGalleries } from "../../hooks/use-get-all-galleries";
import type {
  FloorPlanGalleryContextValue,
  FloorPlanGalleryProviderProps,
} from "./types";

const initialData: FloorPlanGalleryContextValue = {
  floorPlans: null,
  isLoading: true,
};

const FloorPlanGalleryContext = React.createContext(initialData);

function useFloorPlanGallery() {
  return React.useContext(FloorPlanGalleryContext);
}

function FloorPlanGalleryProvider({ children }: FloorPlanGalleryProviderProps) {
  const result = useGetAllFloorPlanGalleries();

  const isLoading = React.useMemo(() => {
    return result.some(r => r.isLoading);
  }, [result]);

  const floorPlans = React.useMemo(() => {
    if (result.some(r => r.isLoading)) {
      return null;
    }

    if (result.length === 0) {
      return null;
    }

    const formatted = result.map(({ data }) => data ?? {});

    return formatted.reduce(
      (acc, cur) => ({
        ...acc,
        ...cur,
      }),
      {},
    );
  }, [result]);

  const context = React.useMemo(
    () => ({ floorPlans, isLoading }),
    [floorPlans, isLoading],
  );

  return (
    <FloorPlanGalleryContext.Provider value={context}>
      {children}
    </FloorPlanGalleryContext.Provider>
  );
}

export default React.memo(FloorPlanGalleryProvider);
export { useFloorPlanGallery };
